import {ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild,} from '@angular/core';
import {RouterModule} from '@angular/router'; // ✅ Import RouterModule
import {FormsModule} from '@angular/forms'; // Keep if you're using ngModel
import {
    DsBoxModule,
    DsBreadcrumbModule,
    DsButtonModule,
    DsCheckboxModule,
    DsFormFieldModule,
    DsIconModule,
    DsLoadingAreaConfiguration,
    DsLoadingAreaService,
    DsModalModule,
    DsProgressCircleModule,
    DsSelectModule,
    DsTableModule,
    DsTabsModule,
} from '@bmw-ds/components';
import {FiltersComponent} from 'src/app/components/filters/filters.component';
import {BreadcrumbComponent} from 'src/app/components/breadcrumb/breadcrumb.component';
import {ResultsCountComponent} from 'src/app/components/results-count/results-count.component';
import {TabsComponent} from 'src/app/components/tabs/tabs.component';
import {CriteriaService} from 'src/app/services/criteria.service';
import {CriteriaStoreService} from 'src/app/services/criteria-store.service';
import {CriteriaResponse} from 'src/app/models/criteria.model';
import {RequestPayload} from 'src/app/models/stock-search-payload.model';
import {CommonModule} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {StockService} from '../../../services/stock.service';
import {StocksheetRequest} from '../../../models/stocksheet-request.model';
import {AuthService} from '../../../services/auth.service';
import {Subject} from 'rxjs';
import {finalize, take, takeUntil} from 'rxjs/operators';

@Component({
    selector: 'app-stock-unit-search',
    standalone: true,
    imports: [
        TabsComponent,
        CommonModule,
        RouterModule,
        DsTabsModule,
        FormsModule,
        DsBreadcrumbModule,
        DsTableModule,
        DsSelectModule,
        DsFormFieldModule,
        DsCheckboxModule,
        DsProgressCircleModule,
        FiltersComponent,
        BreadcrumbComponent,
        ResultsCountComponent,
        DsButtonModule,
        DsIconModule,
        DsBoxModule,
        DsModalModule,
    ],
    templateUrl: './stock-unit-search.component.html',
    styleUrl: './stock-unit-search.component.scss',
})
export class StockUnitSearchComponent implements OnInit, OnDestroy {

    criteriaData!: CriteriaResponse;
    requestPayload!: RequestPayload;
    loadingAreaConfig: DsLoadingAreaConfiguration = {
        id: 'bodyLoading',
        loadingTitle: 'Loading...',
    };
    stockSheetModalIsOpen: boolean = false;
    stocksheetRequest: StocksheetRequest = {
        dealerCode: localStorage.getItem('dealerCode') ?? '',
        plantCode: '',
    };
    selectedModel: string | null = null;
    criteriaLoadError: string | null = null;
    downloadError: string | null = null;
    private lastDealerCode: string = '';

    onModelSelected(event: string) {
        this.selectedModel = event;
    }

    onFiltersChanged(payload: RequestPayload): void {
        console.log('payload', payload);
        this.requestPayload = payload;
    }

    private readonly criteriaService = inject(CriteriaService);
    private readonly stockService = inject(StockService);
    private readonly criteriaStoreService = inject(CriteriaStoreService);
    private readonly loadingAreaService = inject(DsLoadingAreaService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly authService = inject(AuthService);
    private destroy$ = new Subject<void>();

    ngOnInit(): void {
        this.authService.isAuthenticated().pipe(take(1)).subscribe((isAuthenticated) => {
            if (isAuthenticated) {
                this.loadCriteriaData(localStorage.getItem('dealerCode') ?? ''); // Default dealer code if not set
            } else {
                window.location.href = this.authService.getAuthUrl(); // Redirect to the auth URL
            }
        });
    }

    loadCriteriaData(dealerCode: string): void {
        this.lastDealerCode = dealerCode;
        this.criteriaLoadError = null;
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        this.criteriaService.postCriteriaSelection(dealerCode)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id))
            )
            .subscribe({
                next: (data: CriteriaResponse) => {
                    this.criteriaData = data;
                    localStorage.setItem('plantCode', this.criteriaData.plant);
                    this.criteriaStoreService.setCriteriaData(data);
                    this.cdr.detectChanges();
                },
                error: (error: unknown) => {
                    this.criteriaLoadError = this.toErrorMessage(error, 'Unable to load criteria data.');
                    console.error('Error fetching criteria data', error);
                }
            });
    }

    retryCriteriaLoad(): void {
        this.loadCriteriaData(this.lastDealerCode || (localStorage.getItem('dealerCode') ?? ''));
    }

    downloadStockSheet() {
        this.stockSheetModalIsOpen = false;
        this.downloadError = null;
        this.stocksheetRequest.plantCode = localStorage.getItem('plantCode') ?? '';
        this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
        this.stockService.downloadStockSheet(this.stocksheetRequest)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id))
            )
            .subscribe({
                next: (response: Blob) => {
                    const fileName = `StockSheet_${new Date().toISOString()}.xlsx`;
                    const blob = new Blob([response], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    });
                    const link = document.createElement('a');
                    link.href = window.URL.createObjectURL(blob);
                    link.download = fileName;
                    link.click();
                    window.URL.revokeObjectURL(link.href);
                    this.cdr.detectChanges();
                },
                error: (error: HttpErrorResponse) => {
                    this.downloadError = this.toErrorMessage(error, 'Unable to download stock sheet.');
                    console.log(error);
                }
            });
    }

    openStockSheetModal() {
        this.stockSheetModalIsOpen = true;
    }

    @ViewChild('filtersComp') filtersComp!: FiltersComponent;

    showFilters: boolean = true;
    showTabs: boolean = false;

    toggleFilterVisibility(): void {
        this.showFilters = !this.showFilters;
    }

    clearAllFilters(): void {
        if (this.filtersComp) {
            this.filtersComp.clearFilters();
        }
    }

    @ViewChild(TabsComponent) tabsComp!: TabsComponent;

    searchClicked() {
        this.showTabs = true;
        setTimeout(() => {
            if (this.tabsComp) {
                this.tabsComp.triggerActiveTabAction();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private toErrorMessage(error: unknown, fallback: string): string {
        if (error instanceof HttpErrorResponse) {
            if (error.status === 0) {
                return 'Network error. Check your connection and try again.';
            }
            const serverMessage = (error.error && (error.error.message || error.error.error)) as string | undefined;
            if (serverMessage) {
                return serverMessage;
            }
            if (error.status) {
                return `${fallback} (HTTP ${error.status})`;
            }
        }
        return fallback;
    }
}
